import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';

import { authApi } from '@/api/auth.api';
import { extractApiError } from '@/api/client';
import { getIsosoftsToken, ISOSOFTS_BASE_URL, redirectToIsosoftsLogin } from '@/lib/isosofts';
import { useAuthStore } from '@/stores/auth.store';

type ProtectedRouteProps = {
  children: ReactNode;
};

function FullScreen({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center text-muted text-sm">{children}</div>
  );
}

function AccessDenied() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center px-6">
      <div className="text-lg font-semibold text-foreground">No access to Documentology</div>
      <p className="text-sm text-muted max-w-sm">
        Your IsoSofts account does not have the “Documentology” module enabled. Please ask your administrator
        to grant access.
      </p>
      <a href={ISOSOFTS_BASE_URL} className="text-sm font-medium text-primary hover:underline">
        Back to IsoSofts
      </a>
    </div>
  );
}

/**
 * Gate for the authenticated ESD app. Identity root is IsoSofts:
 *   • If an ESD session already exists → render (hydrate user via /auth/me if needed).
 *   • Else, if the shared IsoSofts `auth_token` cookie is present → exchange it for an
 *     ESD session via /auth/sso.
 *   • No cookie and no session → redirect to the central IsoSofts login.
 * Public signing routes (/sign, /verify) do NOT use this gate — external signers are
 * not IsoSofts users.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, refreshToken, isosoftsToken: sessionIsosoftsToken, setSession, setUser, clear } =
    useAuthStore();
  const cookieToken = getIsosoftsToken();
  const hasSession = !!refreshToken;

  // The persisted ESD session is only trustworthy if it was minted from the SAME
  // IsoSofts cookie that is present right now. localStorage is per-origin, so a
  // logout / account switch performed on another suite app (e.g. algebra.isosofts.com)
  // cannot clear ESD's store — without this check ESD would happily show the previous
  // account. A missing cookie (logged out) or a different token (new account) makes
  // the session stale.
  const sessionMatchesCookie = hasSession && !!cookieToken && cookieToken === sessionIsosoftsToken;

  // Drop a stale session so it can be re-exchanged (cookie present) or end in a
  // redirect (cookie absent).
  useEffect(() => {
    if (hasSession && cookieToken !== sessionIsosoftsToken) {
      clear();
    }
  }, [hasSession, cookieToken, sessionIsosoftsToken, clear]);

  // SSO exchange: have an IsoSofts cookie but no matching ESD session yet.
  const ssoEnabled = !sessionMatchesCookie && !!cookieToken;
  const sso = useQuery({
    queryKey: ['auth', 'sso', cookieToken],
    queryFn: () => authApi.sso(cookieToken as string),
    enabled: ssoEnabled,
    retry: false,
  });
  useEffect(() => {
    if (sso.data) {
      setSession(
        {
          accessToken: sso.data.accessToken,
          refreshToken: sso.data.refreshToken,
          expiresIn: sso.data.expiresIn,
        },
        sso.data.user,
        cookieToken as string,
      );
    }
  }, [sso.data, setSession, cookieToken]);

  // Returning visitor: matching ESD session but no cached user → hydrate.
  const shouldHydrate = sessionMatchesCookie && !user;
  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    enabled: shouldHydrate,
    retry: false,
  });
  useEffect(() => {
    if (me.data) setUser(me.data);
  }, [me.data, setUser]);
  useEffect(() => {
    if (shouldHydrate && me.isError) clear();
  }, [shouldHydrate, me.isError, clear]);

  // Identity refresh: the cached user in localStorage survives until the suite cookie
  // changes, but IsoSofts can change an account's email WITHOUT invalidating its
  // session — so the cookie (and with it the SSO exchange, and the cached user) stays
  // put while the address is already stale. Pull it on every page load: the backend
  // updates the ESD row from IsoSofts and returns the fresh profile.
  const identity = useQuery({
    queryKey: ['auth', 'sync-identity', cookieToken],
    queryFn: () => authApi.syncIdentity(cookieToken as string),
    enabled: sessionMatchesCookie && !!user,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (identity.data) setUser(identity.data);
  }, [identity.data, setUser]);

  // No matching session and nothing to exchange → central login.
  if (!sessionMatchesCookie && !cookieToken) {
    redirectToIsosoftsLogin();
    return <FullScreen>Redirecting to IsoSofts…</FullScreen>;
  }

  if (ssoEnabled && sso.isLoading) {
    return <FullScreen>Connecting to IsoSofts…</FullScreen>;
  }
  if (ssoEnabled && sso.isError) {
    const code = extractApiError(sso.error).code;
    if (code === 'NO_ESD_ACCESS') return <AccessDenied />;
    redirectToIsosoftsLogin();
    return <FullScreen>Redirecting to IsoSofts…</FullScreen>;
  }

  if (shouldHydrate && me.isLoading) {
    return <FullScreen>Loading session…</FullScreen>;
  }
  if (shouldHydrate && me.isError) {
    redirectToIsosoftsLogin();
    return <FullScreen>Redirecting to IsoSofts…</FullScreen>;
  }

  // SSO succeeded but the store update has not flushed into this render yet.
  if (!sessionMatchesCookie) {
    return <FullScreen>Connecting to IsoSofts…</FullScreen>;
  }

  return <>{children}</>;
}
